import { useState, useEffect, useId, useMemo, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { SECTIONS } from './nav-config';
import { useLevels } from '@/shared/permissions/hooks';
import { useAuthStore } from '@/shared/store/authStore';
import { IconSearch } from '@/components/ui/Icon';
import { useFocusTrap } from '@/components/ui/useFocusTrap';

// Global command palette: fuzzy-jump to any page + quick "New …" actions.
// Opened with Ctrl/Cmd+Shift+P, or by dispatching window event
// 'nexus:command-palette' (the top-nav launcher button). Mounted once in
// AppLayout. ⌘K stays the AI Companion; this is the keyboard navigator.

type Group = 'Create' | 'Go to';
type Entry = { id: string; label: string; hint: string; to: string; group: Group; haystack: string };

// Quick create actions → the new-record form routes. Each is gated on WRITE of
// its catalog node so users only see what they can actually create.
const QUICK_NEW: Array<{ label: string; to: string; node: string }> = [
  { label: 'New invoice',        to: '/ar-invoices/new',           node: 'receivables.invoices' },
  { label: 'New credit note',    to: '/credit-notes/new',          node: 'receivables.credit_notes' },
  { label: 'New receipt',        to: '/receipts/new',              node: 'receivables.receipts' },
  { label: 'New customer',       to: '/customers/new',             node: 'sales.customers' },
  { label: 'New quote',          to: '/quotes/new',                node: 'sales.orders' },
  { label: 'New sales order',    to: '/sales-orders/new',          node: 'sales.orders' },
  { label: 'New shipment',       to: '/shipments/new',             node: 'sales.shipments' },
  { label: 'New vendor',         to: '/vendors/new',               node: 'payables.vendors' },
  { label: 'New purchase order', to: '/purchase-orders/new',       node: 'payables.purchase_orders' },
  { label: 'New requisition',    to: '/purchase-requisitions/new', node: 'payables.purchase_requisitions' },
  { label: 'New bill',           to: '/ap-invoices/new',           node: 'payables.bills' },
  { label: 'New payment',        to: '/payments/new',              node: 'payables.payments' },
  { label: 'New item',           to: '/items/new',                 node: 'products.items' },
  { label: 'New journal entry',  to: '/journal/new',               node: 'ledger.journal' },
  { label: 'New project',        to: '/projects/new',              node: 'projects.projects' },
];

// Flattened nav (static) — every sub-item across all sections. Sub-items that
// are group headers (they carry `children`, e.g. Admin Center → Master Data /
// Customization) expand to their child leaves so those destinations stay
// searchable; the header itself isn't a page, so it's dropped.
const NAV_ITEMS = SECTIONS.flatMap((s) =>
  s.sub.flatMap((it) => (it.children && it.children.length ? it.children : [it]).map((leaf) => ({ ...leaf, section: s.label }))),
);
// Every catalog node we need a permission level for (stable array for useLevels).
const ALL_NODES: string[] = Array.from(new Set<string>([
  ...NAV_ITEMS.map((e) => e.node).filter((n): n is string => !!n),
  ...QUICK_NEW.map((q) => q.node),
]));

function score(haystack: string, label: string, q: string, toks: string[]): number | null {
  let s = 0;
  for (const t of toks) { const i = haystack.indexOf(t); if (i < 0) return null; s += i; }
  if (label.toLowerCase().startsWith(q)) s -= 100; // prefer prefix matches on the label
  return s;
}

export function CommandPalette() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  // Where focus was when the palette opened: Escape hands it back there.
  const openerRef = useRef<HTMLElement | null>(null);
  const listId = useId();
  const optionId = (i: number) => `${listId}-opt-${i}`;
  // aria-modal means Tab must not leave the panel. The results are option divs
  // (not Tab stops), so Tab wraps on the search box. The palette is portaled to
  // body, so focus landing in #root (the page behind) is pulled back in.
  const panelRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(panelRef, { active: open });

  const levels = useLevels(ALL_NODES);
  const roles = useAuthStore((s) => s.user?.roles ?? []);
  const company = useAuthStore((s) => s.company);

  // Open via Ctrl/Cmd+Shift+P, or the custom launcher event. Esc closes (handled
  // on the input). The combo is deliberate, so we let it fire anywhere.
  useEffect(() => {
    const remember = () => {
      const a = document.activeElement;
      openerRef.current = a instanceof HTMLElement && a !== document.body ? a : null;
    };
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
        setOpen((o) => {
          if (!o) { remember(); return true; }
          openerRef.current?.focus(); // closing by the shortcut hands focus back too
          return false;
        });
      }
    }
    function onOpen() { remember(); setOpen(true); }
    window.addEventListener('keydown', onKey);
    window.addEventListener('nexus:command-palette', onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('nexus:command-palette', onOpen);
    };
  }, []);

  useEffect(() => { if (open) { setQuery(''); setActive(0); setTimeout(() => inputRef.current?.focus(), 0); } }, [open]);
  useEffect(() => { setActive(0); }, [query]);

  const entries = useMemo<Entry[]>(() => {
    const out: Entry[] = [];
    for (const q of QUICK_NEW) {
      if (levels[q.node] === 'write') {
        out.push({ id: `new:${q.to}`, label: q.label, hint: 'Create', to: q.to, group: 'Create', haystack: `${q.label} create new`.toLowerCase() });
      }
    }
    const seen = new Set<string>();
    for (const it of NAV_ITEMS) {
      if (it.requiresRole && !roles.includes(it.requiresRole)) continue;
      if (it.requiresCompanyFlag && company?.[it.requiresCompanyFlag] !== true) continue;
      if (it.node && levels[it.node] === 'hidden') continue;
      if (seen.has(it.to)) continue;
      seen.add(it.to);
      out.push({ id: `go:${it.to}`, label: it.label, hint: it.section, to: it.to, group: 'Go to', haystack: `${it.section} ${it.label}`.toLowerCase() });
    }
    return out;
  }, [levels, roles, company]);

  const filtered = useMemo<Entry[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries.slice(0, 60);
    const toks = q.split(/\s+/);
    return entries
      .map((e) => { const s = score(e.haystack, e.label, q, toks); return s === null ? null : { e, s }; })
      .filter((x): x is { e: Entry; s: number } => x !== null)
      .sort((a, b) => a.s - b.s)
      .slice(0, 60)
      .map((x) => x.e);
  }, [entries, query]);

  // Keep the active row in view as the user arrows through.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-cmd-idx="${active}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [active, filtered]);

  if (!open) return null;

  function go(entry: Entry | undefined) {
    if (!entry) return;
    setOpen(false);
    nav(entry.to);
  }

  function onInputKey(e: ReactKeyboardEvent) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); go(filtered[active]); }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); openerRef.current?.focus(); }
  }

  return createPortal(
    <div className="fixed inset-0 z-popover flex items-start justify-center pt-[12vh] px-4"
      onMouseDown={() => setOpen(false)}>
      <div className="absolute inset-0 bg-black/30" />
      <div
        ref={panelRef}
        role="dialog" aria-modal="true" aria-label="Command palette"
        className="relative w-full max-w-xl rounded-xl border border-border bg-surface shadow-2xl overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-3 h-11 border-b border-border-light">
          <IconSearch size={15} />
          <input
            ref={inputRef}
            role="combobox"
            aria-label="Jump to a page or create a record"
            aria-autocomplete="list"
            aria-expanded={filtered.length > 0}
            aria-controls={listId}
            aria-activedescendant={filtered[active] ? optionId(active) : undefined}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            placeholder="Jump to a page or create a record…"
            className="flex-1 bg-transparent outline-none text-[0.8125rem] text-primary-text placeholder:text-muted"
          />
          <kbd className="hidden sm:inline text-[0.625rem] text-muted border border-border-light rounded px-1.5 py-0.5">Esc</kbd>
        </div>

        <div ref={listRef} id={listId} role="listbox" aria-label="Pages and actions" className="max-h-[52vh] overflow-auto py-1">
          {filtered.length === 0 && (
            <div role="status" className="px-3 py-6 text-center text-[0.75rem] text-muted">No matches for “{query}”.</div>
          )}
          {filtered.map((e, i) => (
            <div
              key={e.id}
              role="option"
              id={optionId(i)}
              aria-selected={i === active}
              data-cmd-idx={i}
              onMouseEnter={() => setActive(i)}
              onClick={() => go(e)}
              className={`w-full flex items-center cursor-pointer gap-2 px-3 py-2 text-left text-[0.8125rem] transition ${
                i === active ? 'bg-primary/10 text-primary-text' : 'text-primary-text hover:bg-slate-50'
              }`}
            >
              <span className={`text-[0.625rem] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 shrink-0 ${
                e.group === 'Create' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
              }`}>{e.group === 'Create' ? 'New' : 'Go'}</span>
              <span className="flex-1 min-w-0 truncate">{e.label}</span>
              <span className="text-meta text-muted shrink-0">{e.hint}</span>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3 px-3 h-8 border-t border-border-light text-meta text-muted">
          <span><kbd className="font-numeric">↑↓</kbd> navigate</span>
          <span><kbd className="font-numeric">↵</kbd> open</span>
          <span className="ml-auto">Command palette</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
